import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MoonIcon,
  LightBulbIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowsRightLeftIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { transitService, TransitInfluence } from '../../services/transitApi';

// Um ícone por aspecto (não só por qualidade), pra cada influência ter uma
// identidade visual própria em vez de repetir a mesma forma pra tudo que é
// "bom" ou "ruim".
const ASPECT_STYLE: Record<string, { badge: string; icon: React.ComponentType<React.SVGProps<SVGSVGElement>> }> = {
  'Sextil':     { badge: 'bg-emerald-500/20 text-emerald-300', icon: LightBulbIcon },
  'Trígono':    { badge: 'bg-emerald-500/20 text-emerald-300', icon: CheckCircleIcon },
  'Quadratura': { badge: 'bg-red-500/20 text-red-300', icon: ExclamationTriangleIcon },
  'Oposição':   { badge: 'bg-red-500/20 text-red-300', icon: ArrowsRightLeftIcon },
  'Conjunção':  { badge: 'bg-indigo-500/20 text-indigo-300', icon: SparklesIcon },
};
const DEFAULT_ASPECT_STYLE = { badge: 'bg-indigo-500/20 text-indigo-300', icon: SparklesIcon };

const formatDate = (iso: string) => {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
};

const InfluenceItem: React.FC<{ influence: TransitInfluence }> = ({ influence }) => {
  const [expanded, setExpanded] = useState(false);
  const style = ASPECT_STYLE[influence.aspect] || DEFAULT_ASPECT_STYLE;
  const Icon = style.icon;
  const houseTxt = influence.transiting_house ? ` (passando pela sua Casa ${influence.transiting_house})` : '';
  const techLine = `${influence.transiting_planet} está aos ${influence.transiting_degree.toFixed(0)}° de ${influence.transiting_sign}${houseTxt}, em ${influence.aspect} ao seu ${influence.natal_point} natal aos ${influence.natal_degree.toFixed(0)}° de ${influence.natal_sign}`;

  return (
    <div className="flex gap-3">
      <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${style.badge}`}>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-semibold text-white">{influence.headline}</h4>
        <p className="text-xs text-gray-400 mb-1">
          De {formatDate(influence.start_date)} a {formatDate(influence.end_date)}
        </p>
        <p className="text-xs text-gray-500 mb-1">{techLine}</p>
        <p className="text-xs text-gray-300 leading-relaxed">
          {expanded ? influence.text : `${influence.text.slice(0, 90)}${influence.text.length > 90 ? '…' : ''}`}
          {influence.text.length > 90 && (
            <button
              onClick={() => setExpanded(v => !v)}
              className="ml-1 text-gold-400 hover:text-gold-300 font-semibold"
            >
              {expanded ? 'Ler menos' : 'Ler mais'}
            </button>
          )}
        </p>
      </div>
    </div>
  );
};

const TransitoPlanetario: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<{ has_birth_chart: boolean; active: TransitInfluence[]; upcoming: TransitInfluence[]; upcoming_window_days: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [showUpcoming, setShowUpcoming] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    transitService.getMyTransits()
      .then(setData)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const VISIBLE_COUNT = 2;

  return (
    <div
      className="backdrop-blur-sm rounded-xl p-6 border border-white/20 flex flex-col lg:flex-1 lg:min-h-0"
      style={{ background: 'rgba(255,255,255,0.05)' }}
    >
      <div className="flex items-center space-x-2 mb-4 flex-shrink-0">
        <MoonIcon className="w-6 h-6 text-purple-300" />
        <h3 className="text-lg font-semibold text-white">Trânsito Planetário</h3>
      </div>

      <div className="lg:flex-1 lg:min-h-0 overflow-y-auto hide-scrollbar pr-1 flex flex-col">
      {/* my-auto: centraliza o bloco quando o conteúdo é mais curto que o box; quando
          é maior e precisa rolar, a margem automática colapsa e o alinhamento volta
          ao normal (sem precisar de justify-content: safe center, que não tem
          suporte confiável em flexbox). */}
      {loading ? (
        <p className="text-gray-400 text-sm my-auto">Calculando seus trânsitos…</p>
      ) : error ? (
        <p className="text-red-300 text-sm my-auto">Não foi possível carregar seus trânsitos agora.</p>
      ) : !data?.has_birth_chart ? (
        <p className="text-gray-400 text-sm my-auto">Complete seus dados de nascimento para ver seus trânsitos planetários personalizados.</p>
      ) : data.active.length === 0 ? (
        <p className="text-gray-400 text-sm my-auto">Nenhuma influência ativa no momento.</p>
      ) : (
        <div className="my-auto">
          <p className="text-sm text-white mb-3">
            Você tem <span className="font-bold text-gold-300">{data.active.length}</span> influência{data.active.length !== 1 ? 's' : ''} ativa{data.active.length !== 1 ? 's' : ''}
          </p>
          <div className="space-y-4">
            {(showAll ? data.active : data.active.slice(0, VISIBLE_COUNT)).map(inf => (
              <InfluenceItem key={inf.id} influence={inf} />
            ))}
          </div>
          {!showAll && data.active.length > VISIBLE_COUNT && (
            <button
              onClick={() => setShowAll(true)}
              className="mt-3 text-xs font-semibold text-gold-400 hover:text-gold-300 transition-colors"
            >
              Ver mais {data.active.length - VISIBLE_COUNT} influências
            </button>
          )}

          {data.upcoming.length > 0 && (
            <div className="mt-5 pt-4 border-t border-white/10">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-gray-400">
                  Você tem <span className="font-semibold text-white">{data.upcoming.length}</span> influência{data.upcoming.length !== 1 ? 's' : ''} entrando nos próximos {data.upcoming_window_days} dias.
                </p>
                <button
                  onClick={() => setShowUpcoming(v => !v)}
                  className="flex-shrink-0 px-3 py-1.5 rounded-full border border-purple-400/40 text-purple-300 text-xs font-semibold hover:bg-purple-500/10 transition-colors"
                >
                  {showUpcoming ? 'Ocultar' : 'Ver influências'}
                </button>
              </div>
              {showUpcoming && (
                <div className="mt-4 space-y-4">
                  {data.upcoming.map(inf => (
                    <InfluenceItem key={inf.id} influence={inf} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      </div>

      <button
        onClick={() => navigate('/astrologia')}
        className="mt-4 text-xs font-semibold text-gold-400 hover:text-gold-300 transition-colors flex-shrink-0"
      >
        Ver mapa astral completo →
      </button>
    </div>
  );
};

export default TransitoPlanetario;
